import React, { useState } from 'react';
import styled from 'styled-components';
import {
  CreateVenueBulkItemResult,
  VenueBulkResultStatus,
  VenueConflictOpeningHour,
} from '../../../shared/dto/EntityRepository';
import { Language, VenueType } from '../../../shared/enum/EnumRepository';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import {
  BlackFitBtn,
  PaperBlurFitBtn,
} from '../../../shared/component/headless/button/BtnBody';
import { VENUE_TYPE_LABELS } from '../../../shared/util/venueTypeLabel';
import {
  VenueConflictChoice,
  VenueConflictChoices,
  VenueConflictField,
  getVenueConflictFields,
  selectVenueConflictsToApply,
} from '../../../shared/util/venueBulk';
import { useAdminVenue } from '../../share/hook/useAdminVenue';

const STATUS_LABEL: Record<VenueBulkResultStatus, string> = {
  CREATED: 'Created',
  SKIPPED_DUPLICATE: 'Skipped',
  CONFLICT: 'Conflict',
  INVALID: 'Invalid',
  UPDATED: 'Updated',
};

const FIELD_LABEL: Record<VenueConflictField, string> = {
  name: 'Name',
  nameEn: 'English name',
  type: 'Type',
  countryCode: 'Country',
  city: 'City',
  district: 'District',
  roadAddress: 'Address',
  roadAddressEn: 'English address',
  latitude: 'Latitude',
  longitude: 'Longitude',
  timeZone: 'Time zone',
  instagramUrl: 'Instagram',
  websiteUrl: 'Website',
  phoneNumber: 'Phone',
  description: 'Description',
  descriptionEn: 'English description',
  admissionType: 'Admission',
  admissionNote: 'Admission note',
  openingHours: 'Opening hours',
  openingHoursNote: 'Hours note',
  naverMapUrl: 'Naver map',
  googleMapUrl: 'Google map',
  venueTag: 'Tag',
};

const DAY_LABEL: Record<string, string> = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

const formatOpeningHours = (hours: VenueConflictOpeningHour[]) => {
  if (hours.length === 0) return 'No hours';
  return hours.map((hour) => {
    const day = DAY_LABEL[hour.dayOfWeek] ?? hour.dayOfWeek;
    if (hour.closed) return `${day} closed`;
    return `${day} ${hour.opensAt ?? ''}–${hour.closesAt ?? ''}`;
  }).join(', ');
};

const formatDiffValue = (
  field: VenueConflictField,
  value: unknown,
) => {
  if (field === 'openingHours') {
    return formatOpeningHours((value as VenueConflictOpeningHour[] | null | undefined) ?? []);
  }
  if (field === 'type' && typeof value === 'string' && value in VENUE_TYPE_LABELS) {
    return VENUE_TYPE_LABELS[value as VenueType][Language.EN];
  }
  if (value == null || value === '') return '—';
  return String(value);
};

const isVenueFieldConflict = (item: CreateVenueBulkItemResult) =>
  item.status === 'CONFLICT'
  && !!item.venueConflict
  && getVenueConflictFields(item.venueConflict).length > 0;

const VenueBulkResult: React.FC = () => {
  const {
    bulkResult,
    bulkItems,
    setBulkResult,
    setBulkItems,
    selectVenueById,
    resolveBulkVenueConflicts,
  } = useAdminVenue();
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [fieldChoices, setFieldChoices] = useState<Record<number, VenueConflictChoices>>({});
  const [applyingConflicts, setApplyingConflicts] = useState(false);
  if (!bulkResult) return null;

  const rows = issuesOnly
    ? bulkResult.results.filter((item) =>
      item.status === 'CONFLICT' || item.status === 'INVALID')
    : bulkResult.results;
  const updatedCount = bulkResult.results.filter((item) => item.status === 'UPDATED').length;

  const chooseField = (
    rowNumber: number,
    field: VenueConflictField,
    choice: VenueConflictChoice,
  ) => {
    setFieldChoices((current) => ({
      ...current,
      [rowNumber]: {
        ...current[rowNumber],
        [field]: choice,
      },
    }));
  };

  const readyVenueConflicts = selectVenueConflictsToApply(
    bulkResult.results.flatMap((item) => {
      if (!isVenueFieldConflict(item) || !item.venueConflict || !item.venueCode) return [];
      const fields = getVenueConflictFields(item.venueConflict);
      const choices = fieldChoices[item.rowNumber] ?? {};
      if (fields.some((field) => !choices[field])) return [];
      const incoming = bulkItems.find((bulkItem) => bulkItem.rowNumber === item.rowNumber);
      if (!incoming) return [];
      return [{
        rowNumber: item.rowNumber,
        venueId: item.venueConflict.venueId ?? item.venueId,
        venueCode: item.venueCode,
        incoming: incoming.venue,
        conflict: item.venueConflict,
        choices,
      }];
    }),
  );

  const applyVenueConflicts = async () => {
    if (readyVenueConflicts.length === 0 || applyingConflicts) return;
    setApplyingConflicts(true);
    try {
      const applied = await resolveBulkVenueConflicts(readyVenueConflicts);
      if (!applied) return;
      setFieldChoices((current) => {
        const next = { ...current };
        readyVenueConflicts.forEach((item) => {
          delete next[item.rowNumber];
        });
        return next;
      });
    } finally {
      setApplyingConflicts(false);
    }
  };

  return (
    <ResultContainer>
      <Summary>
        {bulkResult.createdCount} created / {bulkResult.duplicateCount} skipped / {updatedCount} updated / {bulkResult.conflictCount} conflict / {bulkResult.invalidCount} error
      </Summary>
      <FilterBar>
        <HeadlessBtn
          value="All"
          handleClick={() => setIssuesOnly(false)}
          StyledBtn={!issuesOnly ? FilterBtnActive : FilterBtnInactive}
        />
        <HeadlessBtn
          value={`Issues (${bulkResult.conflictCount + bulkResult.invalidCount})`}
          handleClick={() => setIssuesOnly(true)}
          StyledBtn={issuesOnly ? FilterBtnActive : FilterBtnInactive}
        />
      </FilterBar>
      <ResultList>
        {rows.map((item) => (
          <ResultRow
            key={`${item.rowNumber}-${item.venueCode ?? ''}`}
            $clickable={item.venueId != null}
            onClick={() => {
              if (item.venueId == null) return;
              selectVenueById(String(item.venueId));
            }}
          >
            <RowMeta>
              Row {item.rowNumber}
              {item.venueCode ? ` · ${item.venueCode}` : ''}
            </RowMeta>
            <Status $issue={item.status === 'CONFLICT' || item.status === 'INVALID'}>
              {STATUS_LABEL[item.status]}
            </Status>
            {item.message && <Message>{item.message}</Message>}
            {isVenueFieldConflict(item) && item.venueConflict && (
              <ConflictBox onClick={(event) => event.stopPropagation()}>
                {getVenueConflictFields(item.venueConflict).map((field) => {
                  const diff = item.venueConflict?.[field];
                  if (!diff) return null;
                  const selected = fieldChoices[item.rowNumber]?.[field];
                  const keepLabel = item.venueId == null ? 'Keep first row' : 'Keep current';
                  const importLabel = item.venueId == null ? 'Use this row' : 'Use imported';
                  return (
                    <div key={field}>
                      <ConflictTitle>{FIELD_LABEL[field]}</ConflictTitle>
                      <CandidateList>
                        <CandidateButton
                          type="button"
                          $selected={selected === 'EXISTING'}
                          onClick={() => chooseField(item.rowNumber, field, 'EXISTING')}
                        >
                          <span>{keepLabel}</span>
                          <small>{formatDiffValue(field, diff.existing)}</small>
                        </CandidateButton>
                        <CandidateButton
                          type="button"
                          $selected={selected === 'INCOMING'}
                          onClick={() => chooseField(item.rowNumber, field, 'INCOMING')}
                        >
                          <span>{importLabel}</span>
                          <small>{formatDiffValue(field, diff.incoming)}</small>
                        </CandidateButton>
                      </CandidateList>
                    </div>
                  );
                })}
              </ConflictBox>
            )}
          </ResultRow>
        ))}
      </ResultList>
      <BtnContainer>
        {bulkResult.results.some(isVenueFieldConflict) && (
          <HeadlessBtn
            value={applyingConflicts
              ? 'Applying…'
              : `Apply selected (${readyVenueConflicts.length})`}
            handleClick={() => { void applyVenueConflicts(); }}
            disabled={readyVenueConflicts.length === 0 || applyingConflicts}
            StyledBtn={BlackFitBtn}
          />
        )}
        <HeadlessBtn
          value="Done"
          handleClick={() => {
            setBulkResult(null);
            setBulkItems([]);
          }}
          StyledBtn={PaperBlurFitBtn}
        />
      </BtnContainer>
    </ResultContainer>
  );
};

const ResultContainer = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  padding-bottom: 40px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Summary = styled.div`
  padding: 8px 0 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const FilterBtnActive = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_04};
`;

const FilterBtnInactive = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const ResultRow = styled.article<{ $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const RowMeta = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Status = styled.div<{ $issue: boolean }>`
  color: ${({ theme, $issue }) =>
    $issue ? theme.colors.color_Gray_02 : theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const Message = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
  word-break: break-word;
`;

const ConflictBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px 0 3px;
`;

const ConflictTitle = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  font-weight: 600;
`;

const CandidateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CandidateButton = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.color_Gray_06 : 'transparent'};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  text-align: left;
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};

  small {
    color: ${({ theme }) => theme.colors.color_Gray_05};
    word-break: break-word;
  }
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
`;

export default VenueBulkResult;
